import * as React from 'react';

import { Tooltip } from '@cimpress/react-components';

const isDisabled = (image: Img, container: HTMLElement | null) => !image.width || !image.height || !container;

interface Img {
    url?: string;
    width?: number;
    height?: number;
}

interface HeaderProps {
    name: string;
    image: Img;
    container: HTMLElement | null;
    addModeEnabled: boolean;
    zoomCanvas: (args: { scroll: number; image: Img; container: HTMLElement | null }) => void;
    centerCanvas: (args: { image: Img; container: HTMLElement | null }) => void;
    toggleAddRulerMode: () => void;
    onUndo: () => void;
    onRedo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    undoEvents: number;
    redoEvents: number;
}

const Header: React.FC<HeaderProps> = ({
    name,
    image,
    container,
    toggleAddRulerMode,
    addModeEnabled,
    zoomCanvas,
    centerCanvas,
    onUndo,
    onRedo,
    canUndo,
    undoEvents,
    canRedo,
    redoEvents,
}) => {
    const zoom = (scroll: number) => {
        if (isDisabled(image, container)) {
            return;
        }
        zoomCanvas({ scroll, image, container });
    };

    const zoomIn = () => {
        zoom(1);
    };

    const zoomOut = () => {
        zoom(-1);
    };

    const fitToScreen = () => {
        if (isDisabled(image, container)) {
            return;
        }
        centerCanvas({ image, container });
    };

    const undo = () => {
        onUndo();
    };

    const redo = () => {
        onRedo();
    };

    const disabled = isDisabled(image, container) ? ' disabled' : '';
    const undoDisabled = !canUndo ? ' disabled' : '';
    const redoDisabled = !canRedo ? ' disabled' : '';
    const rulerActive = addModeEnabled ? ' active' : '';
    const resolveTooltipContent = (tooltipCase: 'undo' | 'redo' | 'ruler' | 'zoomIn' | 'zoomOut' | 'fitToScreen') => {
        const sceneLoaded = image.width && image.height;

        switch (tooltipCase) {
            case 'undo':
                return undoEvents > 0 ? <div>Undo {undoEvents} action(s)</div> : <div>No actions to undo</div>;
            case 'redo':
                return redoEvents > 0 ? <div>Redo {redoEvents} action(s)</div> : <div>No actions to redo</div>;
            case 'ruler':
                if (!sceneLoaded) {
                    return <div>Load a scene to enable ruler tools</div>;
                }
                if (addModeEnabled) {
                    return (
                        <div>
                            <div>Ruler mode active with crosshair guides.</div>
                            <div>Click to start drawing the ruler.</div>
                            <div>Click again to finish.</div>
                            <div>Press ESC to exit drawing mode.</div>
                            <div>Customize crosshair in Design Tools.</div>
                        </div>
                    );
                }
                return <div>Add ruler to measure distances on canvas</div>;
            case 'zoomIn':
                return sceneLoaded ? <div>Zoom in on canvas</div> : <div>Load a scene to enable zoom controls</div>;
            case 'zoomOut':
                return sceneLoaded ? <div>Zoom out on canvas</div> : <div>Load a scene to enable zoom controls</div>;
            case 'fitToScreen':
                return sceneLoaded ? (
                    <div>Fit canvas to screen</div>
                ) : (
                    <div>Load a scene to enable canvas controls</div>
                );
        }
    };

    return (
        <div className='canvas-header'>
            <div className='canvas-header__name'>{name}</div>
            <div className='canvas-header__actions'>
                <Tooltip direction='bottom' contents={resolveTooltipContent('undo')}>
                    <button className={`canvas-header__undo${undoDisabled}`} onClick={undo} disabled={!canUndo}>
                        {undoEvents > 0 && <div className='canvas-header__undo__events'>{undoEvents}</div>}
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/undo-l.svg' />
                    </button>
                </Tooltip>

                <Tooltip direction='bottom' contents={resolveTooltipContent('redo')}>
                    <button className={`canvas-header__redo${redoDisabled}`} onClick={redo} disabled={!canRedo}>
                        {redoEvents > 0 && <div className='canvas-header__redo__events'>{redoEvents}</div>}
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/redo-l.svg' />
                    </button>
                </Tooltip>

                <Tooltip className='ruler-tooltip' direction='bottom' contents={resolveTooltipContent('ruler')}>
                    <button
                        className={`canvas-header__add-ruler${disabled}${rulerActive}`}
                        onClick={toggleAddRulerMode}>
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/ruler-1-l.svg' />
                    </button>
                </Tooltip>

                <Tooltip direction='bottom' contents={resolveTooltipContent('zoomIn')}>
                    <button className={`canvas-header__zoom-in${disabled}`} onClick={zoomIn}>
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/zoom-in-l.svg' />
                    </button>
                </Tooltip>

                <Tooltip direction='bottom' contents={resolveTooltipContent('zoomOut')}>
                    <button className={`canvas-header__zoom-out${disabled}`} onClick={zoomOut}>
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/zoom-out-l.svg' />
                    </button>
                </Tooltip>

                <Tooltip direction='bottom' contents={resolveTooltipContent('fitToScreen')}>
                    <button className={`canvas-header__fit-to-screen${disabled}`} onClick={fitToScreen}>
                        <img className='icon2x' src='https://static.ux.cimpress.io/assets/icons/expand-all-1-l.svg' />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

export default React.memo(Header);
